import { NavLink, useLocation } from "react-router-dom";
import { Compass, CalendarDays, CalendarRange, Sparkles, MoreHorizontal, ShieldCheck, WifiOff } from "lucide-react";
import { useTrip } from "@/lib/store";

const ITEMS = [
  { to: "/", label: "Home", Icon: Compass, id: "home" },
  { to: "/calendar", label: "Calendar", Icon: CalendarRange, id: "calendar" },
  { to: "/trip", label: "Trip", Icon: CalendarDays, id: "trip" },
  { to: "/ideas", label: "Ideas", Icon: Sparkles, id: "ideas" },
  { to: "/more", label: "More", Icon: MoreHorizontal, id: "more" },
];

export function BottomNav() {
  const { pathname } = useLocation();
  return (
    <nav data-testid="bottom-nav" className="fixed bottom-0 inset-x-0 z-40 md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="mx-auto max-w-md border-t bg-[#FBF9F5]/92 backdrop-blur-xl shadow-[0_-8px_24px_rgba(23,56,47,0.06)]">
        <div className="grid grid-cols-5">
          {ITEMS.map(({ to, label, Icon, id }) => {
            const active = to === "/" ? pathname === "/" : to === "/more" ? (pathname.startsWith("/more") || pathname.startsWith("/map")) : pathname.startsWith(to);
            return (
              <NavLink key={id} to={to} data-testid={`nav-item-${id}`} className="tap flex flex-col items-center justify-center gap-1 py-2.5 min-h-[60px]">
                <span className={`flex h-8 w-12 items-center justify-center rounded-full transition-colors ${active ? "bg-[var(--jade)] text-[var(--parchment)]" : "text-[var(--ink-muted)]"}`}>
                  <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.4 : 1.8} />
                </span>
                <span className={`text-[10px] font-semibold tracking-[0.14em] uppercase ${active ? "text-[var(--jade)]" : "text-[var(--ink-muted)]"}`}>{label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

export function TopBar() {
  const { pathname } = useLocation();
  const { organiser, online, pending } = useTrip();
  return (
    <header className="sticky top-0 z-40 hidden md:block border-b bg-[#FBF9F5]/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3">
        <NavLink to="/" className="font-display text-xl font-semibold text-[var(--jade)]">Mary's Birthday China Adventure</NavLink>
        <div className="flex items-center gap-1">
          {ITEMS.map(({ to, label, id }) => {
            const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
            return <NavLink key={id} to={to} data-testid={`desktop-nav-${id}`} className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${active ? "bg-[var(--jade)] text-[var(--parchment)]" : "text-[var(--ink-muted)] hover:bg-[var(--jade-soft)]"}`}>{label}</NavLink>;
          })}
          {organiser && <span className="ml-3 inline-flex items-center gap-1 rounded-full bg-[var(--plum-soft)] px-3 py-1 text-xs font-semibold text-[var(--plum)]"><ShieldCheck className="h-3.5 w-3.5" /> Organiser</span>}
          {!online && <span className="ml-2 inline-flex items-center gap-1 text-xs text-[var(--ink-muted)]"><WifiOff className="h-3.5 w-3.5" /> Offline{pending ? ` · ${pending} to sync` : ""}</span>}
        </div>
      </div>
    </header>
  );
}

export function StatusStrip() {
  const { online, pending, organiser } = useTrip();
  if (online && !pending && !organiser) return null;
  return (
    <div data-testid="status-strip" className="mx-auto flex max-w-md items-center justify-center gap-3 px-4 pt-2 text-[11px] font-semibold md:hidden">
      {organiser && <span className="inline-flex items-center gap-1 rounded-full bg-[var(--plum-soft)] px-3 py-1 text-[var(--plum)]"><ShieldCheck className="h-3 w-3" /> Organiser view</span>}
      {!online && <span className="inline-flex items-center gap-1 rounded-full bg-stone-200 px-3 py-1 text-stone-700"><WifiOff className="h-3 w-3" /> Offline — using saved copy</span>}
      {online && pending > 0 && <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">{pending} change{pending > 1 ? "s" : ""} syncing…</span>}
    </div>
  );
}
